import { type FC, type ReactElement, useEffect, useMemo, useState } from 'react';

import './student-missions.scss';

import { Choose, If, Otherwise, When } from 'vite-plugin-react-control-statements';
import { EmptyState } from '@/components/student-missions/components/empty-state.tsx';
import { MissionTable } from '@/components/student-missions/components/mission-table.tsx';
import { Modal } from '@/components';
import { MissionActions } from '@/components/student-missions/components/mission-actions.tsx';
import DialogModal from '../modal/DialogModal.tsx';
import type { AxiosResponse } from 'axios';
import { useQueryProvider } from '@/hooks';
import type {
	CompetencyResponse,
	PageStudentMissionResponse,
	ResponseDTOListCompetencyResponse,
	ResponseDTOPageStudentMissionResponse,
	ResponseDTOStudentMissionDetailResponse,
	StudentMissionDetailResponse
} from '@dopingtech/live_expert_bff_api_ts/api.ts';
import { useApi } from '@/api/useApi.tsx';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useQueryClient } from 'react-query';
import type { ListFilter } from '@/components/student-missions/student-mission.types.ts';
import LoadingSpinner from '@/components/loading-spinner';
import { MissionDetail } from '@/components/student-missions/components/mission-detail.tsx';
import clsx from 'clsx';

type Props = {
	studentId: number;
	gradeId: number;
};

const DefaultFilter: ListFilter = {
	showOtherExpert: false,
	statuses: ['TODO', 'IN_PROGRESS', 'DONE'],
	page: 0,
	size: 10,
	sort: []
};

const StudentMissions: FC<Props> = ({ studentId, gradeId }: Props): ReactElement<Props> => {
	const queryClient = useQueryClient();
	const { MissionControllerApi } = useApi();

	const [isOpenActionsModal, setIsOpenActionsModal] = useState<boolean>(false);
	const [showCloseWarning, setShowCloseWarning] = useState<boolean>(false);
	const [isChange, setIsChange] = useState<boolean>(false);
	const [studentMissionId, setStudentMissionId] = useState<number | null>(null);
	const [filter, setFilter] = useState<ListFilter>({ ...DefaultFilter });
	const [keyboardHeight, setKeyboardHeight] = useState<number>(0);

	const { data: missionData, isLoading: missionIsLoading } = useQueryProvider<AxiosResponse<ResponseDTOPageStudentMissionResponse>, Error, PageStudentMissionResponse | undefined>(
		[QUERY_KEYS.getStudentMissions, studentId, filter.showOtherExpert, filter.statuses, filter.page, filter.size, filter.sort],
		() => MissionControllerApi.getStudentMissions(studentId, filter.showOtherExpert, filter.statuses, filter.page, filter.size, filter.sort) as any,
		{
			select: ({ data }) => data?.data,
			refetchOnWindowFocus: true,
			enabled: !!studentId,
			staleTime: Infinity
		}
	);

	const { data: missionDetailData, isLoading: missionDetailIsLoading } = useQueryProvider<AxiosResponse<ResponseDTOStudentMissionDetailResponse>, Error, StudentMissionDetailResponse | undefined>(
		[QUERY_KEYS.getStudentMissions, studentMissionId],
		() => MissionControllerApi.getStudentMissionDetail(studentMissionId!) as any,
		{
			select: ({ data }) => data?.data,
			enabled: !!studentMissionId,
			staleTime: Infinity
		}
	);

	const { data: competenciesData } = useQueryProvider<AxiosResponse<ResponseDTOListCompetencyResponse>, Error, CompetencyResponse[] | undefined>(
		[QUERY_KEYS.getCompetencies, studentId],
		() => MissionControllerApi.getCompetencies() as any,
		{
			select: ({ data }) => data?.data,
			refetchOnWindowFocus: true,
			enabled: !!studentId,
			staleTime: Infinity
		}
	);

	const onCloseModalWithoutSave = () => {
		setIsOpenActionsModal(false);
		setShowCloseWarning(false);
	};

	const onMissionCreateComplete = async (): Promise<void> => {
		setIsOpenActionsModal(false);
		setIsChange(false);

		await queryClient.invalidateQueries([QUERY_KEYS.getStudentMissions]);
	};

	const onCloseActionModal = (): void => {
		if (isChange && !studentMissionId) {
			setShowCloseWarning(true);
		} else {
			setIsOpenActionsModal(false);
		}
	};

	const onShowDetail = (studentMissionId: number): void => {
		setStudentMissionId(studentMissionId);
		setIsOpenActionsModal(true);
	};

	const onCreateNewMission = (): void => {
		setStudentMissionId(null);
		setIsOpenActionsModal(true);
	};

	const filterIsDefault = useMemo(() => {
		return JSON.stringify(filter) === JSON.stringify(DefaultFilter);
	}, [filter]);

	useEffect(() => {
		const handleEvent = (event: any) => {
			setKeyboardHeight(event.data);
		};

		document.addEventListener('keyboardHeight', handleEvent);

		return () => document.removeEventListener('keyboardHeight', handleEvent);
	}, [keyboardHeight]);

	return (
		<div className="student-missions">
			<Choose>
				<When condition={filterIsDefault && (!missionData?.content?.length || !competenciesData?.length)}>
					<EmptyState onCreateMission={() => setIsOpenActionsModal(true)} />
				</When>
				<Otherwise>
					<MissionTable
						list={missionData?.content}
						competenciesList={competenciesData}
						totalPages={missionData?.totalPages}
						currentPage={missionData?.number}
						perPageSize={missionData?.size}
						onChangeFilter={setFilter}
						onDetail={onShowDetail}
						onCreateNewMission={onCreateNewMission}
						onPageChange={page => setFilter(prev => ({ ...prev, page: page - 1 }))}
					/>
				</Otherwise>
			</Choose>
			<DialogModal
				isOpen={isOpenActionsModal}
				mobileBottomSheet={true}
				showCloseIcon={false}
				onClose={onCloseActionModal}
				showHeader={false}
				className={clsx('mission-modal', { 'with-keyboard': !!keyboardHeight })}>
				<div className="mission-modal-content">
					<a
						className="close-button"
						onClick={onCloseActionModal}>
						<i className="icons-close" />
					</a>
					{studentMissionId ? (
						<MissionDetail
							missionDetail={missionDetailData}
							isLoading={missionDetailIsLoading}
						/>
					) : (
						<MissionActions
							competenciesList={competenciesData}
							studentId={studentId}
							gradeId={gradeId}
							onChangeAnyField={setIsChange}
							onComplete={onMissionCreateComplete}
						/>
					)}
				</div>
			</DialogModal>
			<Modal
				isOpen={showCloseWarning}
				onClose={onCloseModalWithoutSave}
				closeOnEsc={true}
				closeOnClickOutside={false}
				size="sm"
				className="mission-warning-modal">
				<div className="mission-modal-content">
					<a
						className="close-button"
						onClick={onCloseModalWithoutSave}>
						<i className="icons-close" />
					</a>
					<div className="mission-modal-wrapper">
						<i className="icons-danger" />
						<span className="info-header">Değişiklikleri Kaydetmediniz!</span>
						<span className="info-description">Yaptığınız değişiklikler kaydedilmeyecektir. Kapatmak istediğinize emin misiniz?</span>
						<div className="buttons-wrapper">
							<a
								className="info-grey-btn"
								onClick={() => setShowCloseWarning(false)}>
								Vazgeç
							</a>
							<a
								className="info-red-btn"
								onClick={onCloseModalWithoutSave}>
								Evet
							</a>
						</div>
					</div>
				</div>
			</Modal>
			<If condition={missionIsLoading}>
				<LoadingSpinner loadingText={'Yükleniyor'} />
			</If>
		</div>
	);
};

export default StudentMissions;
